// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { LanguageModel } from "@insite/client-framework/Types/ApiModels";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import RegisterEventButton from "@mcmc/App/Components/RegisterEventButton";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import ProductDescription, { ProductDescriptionStyles } from "@mcmc/Overrides/Components/ProductDescription";
import ProductPrice, { ProductPriceStyles } from "@mcmc/Overrides/Components/ProductPrice";
import { productListStyles } from "@mcmc/Overrides/Widgets/ProductList/ListCommon.styles";
import { productCardListStyles } from "@mcmc/Overrides/Widgets/ProductList/ProductCard.styles";
import StyledBox from "@mcmc/XDL/Components/Common/Box";
import LinkButton from "@mcmc/XDL/Components/Common/LinkButton";
import MultilineEllipsis from "@mcmc/XDL/Components/Common/MultiLineEllipsis";
import { mergeWithCss } from "@mcmc/XDL/Utilities";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: McMcApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    language: state.context.session.language,
});

type Props = ReturnType<typeof mapStateToProps> & HasProductContext;

export interface EventCardStyles {
    cardContainer?: InjectableCss;
    cardInfoWrapper?: InjectableCss;
    eventTitleStyles?: ProductDescriptionStyles;
    attributesWrapper?: InjectableCss;
    attributeText?: TypographyPresentationProps;
    cardActionsWrapper?: InjectableCss;
    eventNumberWrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    price?: ProductPriceStyles;
    summaryWrapper?: InjectableCss;
    courseNumberLabel?: TypographyPresentationProps;
}

export const eventCardStyles: EventCardStyles = {
    cardContainer: {
        css: css`
            padding: 0;
            margin-bottom: 30px;
            &:last-child {
                margin-bottom: 0;
            }
            min-height: 160px;
        `,
    },
    cardInfoWrapper: {
        css: css`
            padding: 25px 30px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 15px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    eventTitleStyles: { productDetailLink: productListStyles.cardTitleLink },
    attributesWrapper: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    attributeText: {
        weight: "bold",
        css: css`
            display: inline;
            font-weight: bold;
            &:after {
                content: " | ";
                color: ${getColor("secondary")};
            }
            &:last-child:after {
                content: "";
            }
        `,
    },
    cardActionsWrapper: {
        css: css`
            flex: 0 0 225px;
            min-width: 0;
            padding: 25px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 15px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    eventNumberWrapper: {
        css: css`
            padding: 5px;
            text-align: right;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            text-align: left;
                            flex: 1 1 100%;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    price: {
        wrapper: {
            css: css`
                padding: 5px;
                margin-left: auto;
                margin-top: auto;
                overflow-wrap: break-word;
                word-wrap: break-word;
                text-align: right;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                flex: 1 1 100%;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            `,
        },
        price: {
            priceText: {
                css: css`
                    white-space: normal;
                `,
            },
        },
    },
    addToCartWrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex: 1 1 100%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    summaryWrapper: {
        css: css`
            height: 48px;
            overflow: hidden;
            display: block;
        `,
    },
    courseNumberLabel: { weight: "bold", transform: "uppercase" },
};

const styles = mergeWithCss(productCardListStyles, eventCardStyles);

const getFormatedDate = (startDate: string, endDate: string, language: LanguageModel | null) => {
    if (!startDate || !endDate) {
        return null;
    }
    const dt1 = new Date(startDate);
    const dt2 = new Date(endDate);

    const startMonth = getLocalizedDateTime({ dateTime: dt1, language, options: { month: "long" } });
    const startDay = getLocalizedDateTime({ dateTime: dt1, language, options: { day: "2-digit" } });
    const endMonth = getLocalizedDateTime({ dateTime: dt1, language, options: { month: "long" } });
    const endDay = getLocalizedDateTime({ dateTime: dt2, language, options: { day: "2-digit" } });
    const startYear = getLocalizedDateTime({ dateTime: dt1, language, options: { year: "numeric" } });
    const endYear = getLocalizedDateTime({ dateTime: dt2, language, options: { year: "numeric" } });

    if (startMonth === endMonth && startYear === endYear) {
        return `${startMonth} ${startDay === endDay ? startDay : `${startDay} - ${endDay}`} , ${startYear}`;
    }
    return `${startMonth} ${startDay} , ${startYear} - ${endMonth} ${endDay} , ${endYear}`;
};

const EventCard = ({ productContext, productSettings, language }: Props) => {
    const { eventEndDateFormatted, eventStartDateFormatted, eventCategory, eventLocation } = productContext.product.properties;
    const formatedDate = getFormatedDate(eventStartDateFormatted, eventEndDateFormatted, language);
    return (
        <StyledBox {...styles.cardContainer}>
            <StyledWrapper {...styles.cardInfoWrapper}>
                <ProductDescription product={productContext} extendedStyles={styles.eventTitleStyles} />
                <StyledWrapper {...styles.attributesWrapper}>
                    {eventCategory && <Typography {...styles.attributeText}>{eventCategory}</Typography>}
                    {formatedDate && <Typography {...styles.attributeText}>{formatedDate}</Typography>}
                    {eventLocation && <Typography {...styles.attributeText}>{eventLocation}</Typography>}
                    {productContext.product.isVariantParent && (
                        <Typography {...styles.attributeText}>{translate("Multiple Sessions Available")} </Typography>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.summaryWrapper}>
                    {productContext.product.content && (
                        <MultilineEllipsis
                            content={productContext.product.content.htmlContent}
                            maxLines={3}
                            forwardAs="p"
                            lineHeight="16px"
                        />
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.cardActionsWrapper}>
                <StyledWrapper {...styles.eventNumberWrapper}>
                    <Typography {...styles.courseNumberLabel}>{translate("Course #")}: </Typography>
                    {productContext.product.productNumber}
                </StyledWrapper>
                <ProductPrice
                    product={productContext}
                    showLabel={false}
                    showSavings={true}
                    showSavingsAmount={productSettings.showSavingsAmount}
                    showSavingsPercent={productSettings.showSavingsPercent}
                    extendedStyles={styles.price}
                />
                <StyledWrapper {...styles.addToCartWrapper}>
                    {productContext.product.isVariantParent ? (
                        <LinkButton {...styles.addToCartButton} href={productContext.productInfo.productDetailPath}>
                            {translate("View Details")}
                        </LinkButton>
                    ) : (
                        <RegisterEventButton extendedStyles={styles.addToCartButton} />
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledBox>
    );
};

export default withProductContext(connect(mapStateToProps)(EventCard));
